import { useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useSendLogoutMutation } from '../features/auth/authApiSlice'
import useAuth from '../hooks/useAuth'
import useHideOnScroll from '../hooks/useHideOnScroll'
import { useTranslation } from 'react-i18next'
import PulseLoader from 'react-spinners/PulseLoader'
import LanguageSwitcher from './LanguageSwitcher'
import { UsersIcon, UserPlusIcon, LogoutIcon } from './DashIcons'

const DASH_REGEX = /^\/dash(\/)?$/
const USERS_REGEX = /^\/dash\/users(\/)?$/

/* The wordmark's mark: the rosette from the card back, reduced to the
   three rings that survive at 22px. */
const Sigil = () => (
    <svg
        className="dash-brand__sigil"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
        focusable="false"
    >
        <circle cx="12" cy="12" r="11" strokeWidth="1" />
        <rect x="5.6" y="5.6" width="12.8" height="12.8" strokeWidth="1" />
        <rect x="5.6" y="5.6" width="12.8" height="12.8" strokeWidth="1" transform="rotate(45 12 12)" />
        <circle cx="12" cy="12" r="1.9" fill="currentColor" stroke="none" />
    </svg>
)

const DashHeader = () => {
    const { username, isManager, isAdmin } = useAuth()
    const { t } = useTranslation()
    const hidden = useHideOnScroll()

    const navigate = useNavigate()
    const { pathname } = useLocation()

    const [sendLogout, { isLoading, isSuccess, isError, error }] = useSendLogoutMutation()

    useEffect(() => {
        if (isSuccess) navigate('/')
    }, [isSuccess, navigate])

    const showUsers = (isManager || isAdmin) && !USERS_REGEX.test(pathname)
    const showNewUser = USERS_REGEX.test(pathname)

    const errClass = isError ? 'errmsg' : 'offscreen'

    return (
        <>
            <p className={errClass}>{error?.data?.message}</p>

            <header className={`dash-header${hidden ? ' dash-header--hidden' : ''}`}>
                <div className="dash-header__bar">
                    {/* The wordmark is also the way back to the spread picker */}
                    <Link
                        to="/dash"
                        className="dash-brand"
                        aria-current={DASH_REGEX.test(pathname) ? 'page' : undefined}
                    >
                        <Sigil />
                        <span className="dash-brand__name">askarkana</span>
                    </Link>

                    <div className="dash-header__end">
                        {username && (
                            <span className="dash-user" title={t('dash.currentUser', { username })}>
                                {username}
                            </span>
                        )}

                        <LanguageSwitcher inline />

                        <nav className="dash-header__nav">
                            {showNewUser && (
                                <button className="dash-nav__item" onClick={() => navigate('/dash/users/new')}>
                                    <UserPlusIcon />
                                    <span className="dash-nav__label">{t('dash.newUser')}</span>
                                </button>
                            )}
                            {showUsers && (
                                <button className="dash-nav__item" onClick={() => navigate('/dash/users')}>
                                    <UsersIcon />
                                    <span className="dash-nav__label">{t('dash.users')}</span>
                                </button>
                            )}
                            {isLoading ? (
                                <PulseLoader color={'#e8c56d'} size={7} />
                            ) : (
                                <button
                                    className="dash-nav__item dash-nav__item--icon"
                                    onClick={sendLogout}
                                    title={t('dash.logout')}
                                    aria-label={t('dash.logout')}
                                >
                                    <LogoutIcon />
                                </button>
                            )}
                        </nav>
                    </div>
                </div>
            </header>
        </>
    )
}

export default DashHeader
